// Copyright Amazon.com Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/*
 * This public interface for parsing the AWS Encryption SDK Message Header Format
 * is provided for the use of the Encryption SDK for JavaScript only.  It can be used
 * as a reference but is not intended to be use by any packages other than the
 * Encryption SDK for JavaScript.
 *
 * See: https://docs.aws.amazon.com/encryption-sdk/latest/developer-guide/message-format.html#header-structure
 */
import { needs } from '@aws-crypto/material-management';
import { decodeEncryptionContextFactory } from './decode_encryption_context';
import { deserializeEncryptedDataKeysFactory } from './deserialize_encrypted_data_keys';
import { deserializeHeaderV1Factory } from './deserialize_header_v1';
import { deserializeHeaderV2Factory } from './deserialize_header_v2';
// To deal with Browser and Node.js I inject a function to handle utf8 encoding.
export function deserializeFactory(toUtf8, SdkSuite) {
    const decodeEncryptionContext = decodeEncryptionContextFactory(toUtf8);
    const deserializeEncryptedDataKeys = deserializeEncryptedDataKeysFactory(toUtf8);
    const deserializeHeaderV1 = deserializeHeaderV1Factory({
        decodeEncryptionContext,
        deserializeEncryptedDataKeys,
        SdkSuite,
    });
    const deserializeHeaderV2 = deserializeHeaderV2Factory({
        decodeEncryptionContext,
        deserializeEncryptedDataKeys,
        SdkSuite,
    });
    /* The first byte holds the message format version.
     * So this maps a version to a deserializer.
     */
    const deserializeMap = new Map([
        /* I have no idea why someone
         * is going to call me with an empty buffer.
         * But since that is clearly not enough data
         * the right thing seems to be to ask for more data.
         * An unknown version can't be invalid.
         */
        [undefined, (_) => false],
        [1, deserializeHeaderV1],
        [2, deserializeHeaderV2],
    ]);
    return {
        deserializeMessageHeader,
        deserializeEncryptedDataKeys,
        decodeEncryptionContext,
    };
    function deserializeMessageHeader(messageBuffer, deserializeOptions = { maxEncryptedDataKeys: false }) {
        const messageFormatVersion = messageBuffer[0];
        const deserializer = deserializeMap.get(messageFormatVersion);
        /* Precondition: A valid deserializer must exist. */
        needs(deserializer, 'Not a supported message format version.');
        return deserializer(messageBuffer, deserializeOptions);
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZGVzZXJpYWxpemVfZmFjdG9yeS5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9kZXNlcmlhbGl6ZV9mYWN0b3J5LnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLG9FQUFvRTtBQUNwRSxzQ0FBc0M7QUFFdEM7Ozs7Ozs7R0FPRztBQUVILE9BQU8sRUFBa0IsS0FBSyxFQUFFLE1BQU0saUNBQWlDLENBQUE7QUFNdkUsT0FBTyxFQUFFLDhCQUE4QixFQUFFLE1BQU0sNkJBQTZCLENBQUE7QUFDNUUsT0FBTyxFQUFFLG1DQUFtQyxFQUFFLE1BQU0sbUNBQW1DLENBQUE7QUFDdkYsT0FBTyxFQUFFLDBCQUEwQixFQUFFLE1BQU0seUJBQXlCLENBQUE7QUFDcEUsT0FBTyxFQUFFLDBCQUEwQixFQUFFLE1BQU0seUJBQXlCLENBQUE7QUFFcEUsZ0ZBQWdGO0FBQ2hGLE1BQU0sVUFBVSxrQkFBa0IsQ0FDaEMsTUFBcUMsRUFDckMsUUFBMEM7SUFFMUMsTUFBTSx1QkFBdUIsR0FBRyw4QkFBOEIsQ0FBQyxNQUFNLENBQUMsQ0FBQTtJQUN0RSxNQUFNLDRCQUE0QixHQUFHLG1DQUFtQyxDQUN0RSxNQUFNLENBQ1AsQ0FBQTtJQUVELE1BQU0sbUJBQW1CLEdBQUcsMEJBQTBCLENBQUM7UUFDckQsdUJBQXVCO1FBQ3ZCLDRCQUE0QjtRQUM1QixRQUFRO0tBQ1QsQ0FBQyxDQUFBO0lBRUYsTUFBTSxtQkFBbUIsR0FBRywwQkFBMEIsQ0FBQztRQUNyRCx1QkFBdUI7UUFDdkIsNEJBQTRCO1FBQzVCLFFBQVE7S0FDVCxDQUFDLENBQUE7SUFFRjs7T0FFRztJQUNILE1BQU0sY0FBYyxHQUFHLElBQUksR0FBRyxDQUFDO1FBQzdCOzs7OztXQUtHO1FBQ0gsQ0FBQyxTQUFTLEVBQUUsQ0FBQyxDQUFhLEVBQXNCLEVBQUUsQ0FBQyxLQUFLLENBQUM7UUFDekQsQ0FBQyxDQUFDLEVBQUUsbUJBQW1CLENBQUM7UUFDeEIsQ0FBQyxDQUFDLEVBQUUsbUJBQW1CLENBQUM7S0FDekIsQ0FBQyxDQUFBO0lBRUYsT0FBTztRQUNMLHdCQUF3QjtRQUN4Qiw0QkFBNEI7UUFDNUIsdUJBQXVCO0tBQ3hCLENBQUE7SUFFRCxTQUFTLHdCQUF3QixDQUMvQixhQUF5QixFQUN6QixxQkFBeUMsRUFBRSxvQkFBb0IsRUFBRSxLQUFLLEVBQUU7UUFFeEUsTUFBTSxvQkFBb0IsR0FBRyxhQUFhLENBQUMsQ0FBQyxDQUFDLENBQUE7UUFDN0MsTUFBTSxZQUFZLEdBQUcsY0FBYyxDQUFDLEdBQUcsQ0FBQyxvQkFBb0IsQ0FBQyxDQUFBO1FBQzdELG9EQUFvRDtRQUNwRCxLQUFLLENBQUMsWUFBWSxFQUFFLHlDQUF5QyxDQUFDLENBQUE7UUFFOUQsT0FBTyxZQUFZLENBQUMsYUFBYSxFQUFFLGtCQUFrQixDQUFDLENBQUE7SUFDeEQsQ0FBQztBQUNILENBQUMifQ==